import { DEFAULT_CHAIN_ID } from 'config'
import { ArtGobblers__factory } from 'contracts'
import { useSmartContractReadCall } from 'hooks/react-query-typechain/useSmartContractReadCall'
import { Attribute, BasicNftGobblerErc721Metadata } from 'pages/api/gobblers/[gobblerId]'
import useSWR, { Fetcher } from 'swr'
import { AvailableGobblerReturnTypes, backendApi } from 'utils/backend-api'
import { useEnsName } from 'wagmi'
import { getRpcProviderForChain } from 'web3/chains'
import { artGobblersOnChainApi } from 'web3/onchain-api/gobblers'
import { useContractAddresses } from './useContractAddresses'
import { useGobblerAssignedId } from './useGobblerAssignedId'

type GobblerDataResult = ReturnType<typeof artGobblersOnChainApi.getGobblerData>

export type GobblerMetadataFromPublicEndpoint = {
  loading: boolean
  gobblerId: string
  revealed: boolean
  minted: boolean
  name: string
  image: string
  attributes: Attribute[]
  assignedId: number
  multiplier: number
  owner: {
    name: string
    address: string
  }
}

function useGobblerMetadataJson(
  gobblerTokenId: string | undefined,
  gobblerContractAddress: string | undefined,
  chainId: string | undefined,
  swrConfigOverrides?: Fetcher<AvailableGobblerReturnTypes> | undefined,
) {
  const assignedGobblerId = useGobblerAssignedId(gobblerTokenId)

  const hasDeps = !!assignedGobblerId && !!chainId && !!gobblerContractAddress
  const { data } = useSWR(
    hasDeps ? [assignedGobblerId, gobblerContractAddress, 'gobbler-metadata-json'] : null,
    backendApi.getGobblerMetadata,
    {
      ...swrConfigOverrides,
    },
  )

  return data
}

function useGobblerExtraInfoQuery(gobblerId: string | undefined) {
  const chainId = DEFAULT_CHAIN_ID
  const contractAddresses = useContractAddresses(chainId)

  const provider = getRpcProviderForChain(DEFAULT_CHAIN_ID)
  const artGobblersContract = contractAddresses?.ART_GOBBLERS
    ? ArtGobblers__factory.connect(contractAddresses.ART_GOBBLERS, provider)
    : undefined

  const { data } = useSmartContractReadCall(artGobblersContract, 'getGobblerData', {
    callArgs: [gobblerId!],
    enabled: !!gobblerId,
  })

  // owner, idx, emissionMultiple
  return data as Awaited<GobblerDataResult> | undefined
}

function useGobblerMetadataFromPublicNftEndpoint(gobblerTokenId: string | undefined) {
  const gobblerExtraInfo = useGobblerExtraInfoQuery(gobblerTokenId)
  const gobblerOwnerAddress = gobblerExtraInfo?.[0] ?? ''

  const assignedRandomId = gobblerExtraInfo?.[1]?.toNumber() ?? -1

  const emissionMultiple = gobblerExtraInfo?.[2]

  const chainId = DEFAULT_CHAIN_ID
  const contractAddresses = useContractAddresses(chainId)

  const { data: ensName } = useEnsName({ address: gobblerOwnerAddress as `0x${string}` })

  const shortenedAddress = gobblerOwnerAddress.slice(0, 6) + '...' + gobblerOwnerAddress.slice(-4)
  const gobblerOwner = gobblerOwnerAddress ? ensName || shortenedAddress : undefined

  const gobblerMetadataFromApi = useGobblerMetadataJson(
    gobblerTokenId,
    contractAddresses?.ART_GOBBLERS,
    chainId,
  )

  let name = 'Loading Gobbler...'
  if (gobblerMetadataFromApi?.revealed === false || emissionMultiple === 0) {
    name = 'Unrevealed Gobbler'
  } else if ((gobblerMetadataFromApi as BasicNftGobblerErc721Metadata | undefined)?.name) {
    name = gobblerMetadataFromApi?.name!
  }

  const gobblerMetadata: GobblerMetadataFromPublicEndpoint = {
    loading: !!gobblerMetadataFromApi !== undefined,
    gobblerId: gobblerTokenId ?? '',
    name: name,
    image: (gobblerMetadataFromApi as BasicNftGobblerErc721Metadata | undefined)?.image
      ? (gobblerMetadataFromApi as BasicNftGobblerErc721Metadata).image
      : '',
    attributes: (gobblerMetadataFromApi as BasicNftGobblerErc721Metadata | undefined)?.attributes
      ? (gobblerMetadataFromApi as BasicNftGobblerErc721Metadata).attributes
      : [],
    multiplier: gobblerExtraInfo?.[2] ?? 0,
    revealed: gobblerMetadataFromApi ? gobblerMetadataFromApi?.revealed : false,
    assignedId: assignedRandomId || -1,
    minted: gobblerMetadataFromApi ? gobblerMetadataFromApi?.minted : false,
    owner: {
      name: gobblerOwner || 'Loading...',
      address: gobblerOwnerAddress,
    },
  }

  return gobblerMetadata
}

export { useGobblerMetadataFromPublicNftEndpoint }
